import React from 'react'
import { useRouter } from 'next/router'
import LoadingIcon from 'shared/icons/loading-icon'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { getTopicPostsByLastId } from 'modules/community/api/posts-api'
import AddPost from 'modules/community/components/manage-post/create-post'
import { PostPinnedList } from 'modules/community/components/pinned-post/post-pinned-list'
import { PostAnnounceSkeleton } from 'modules/community/components/post-announce/components/post-announce-skeleton'
import PostAnnounce from 'modules/community/components/post-announce/post-announce'
import { ScrollToTopButton } from 'modules/community/components/scroll-to-top-button'
import { PostCommentsInfoProvider } from 'modules/community/context/post-comments-info'
import useCommentsInfo from 'modules/community/hooks/use-comments-info'
import { useDeletePost } from 'modules/community/hooks/use-delete-post'
import { useDeletePostAndBanUser } from 'modules/community/hooks/use-delete-post-and-ban'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import { usePostAnnounceList } from 'modules/community/hooks/use-post-announce-list'
import { useToggleLike } from 'modules/community/hooks/use-toggle-like'
import MobileNav from 'modules/header/components/mobile-nav'

function Topic() {
  const router = useRouter()
  const { communityPath, topicPath } = router.query as { communityPath: string; topicPath: string }
  const { isUserMember } = useIsUserMember()

  const postsAnnounceList = usePostAnnounceList({
    fetchPosts: (lastPostId: number | null) =>
      getTopicPostsByLastId(communityPath, topicPath, lastPostId || 0),
    swrKey: 'topic-posts',
    resetDeps: [topicPath],
  })
  const commentsInfoMap = useCommentsInfo({ postIds: postsAnnounceList.posts.map(post => post.id) })
  const handleLikeClick = useToggleLike({
    mutatePosts: postsAnnounceList.mutate,
    communityPath: communityPath,
  })
  const deletePost = useDeletePost({ mutatePosts: postsAnnounceList.mutate })
  const deletePostAndBanUser = useDeletePostAndBanUser({
    mutatePosts: postsAnnounceList.mutate,
    communityPath,
  })

  return (
    <>
      <MobileNav />
      <div>
        {isUserMember && <AddPost />}
        {postsAnnounceList.isLoading && postsAnnounceList.posts.length === 0 && (
          <>
            <PostAnnounceSkeleton key={1} />
            <PostAnnounceSkeleton key={2} />
          </>
        )}
        <PostPinnedList />
        <PostCommentsInfoProvider commentsInfoMap={commentsInfoMap}>
          {postsAnnounceList.posts.map(post => (
            <PostAnnounce
              key={post.id}
              post={post}
              handleLikeClick={handleLikeClick(post)}
              deletePost={deletePost(post.id, postsAnnounceList.posts)}
              deletePostAndBanUser={deletePostAndBanUser(
                post.id,
                postsAnnounceList.posts,
                post.userId,
              )}
            />
          ))}
        </PostCommentsInfoProvider>
        {postsAnnounceList.isLoading && postsAnnounceList.posts.length > 0 && (
          <div className="flex justify-center">
            <LoadingIcon />
          </div>
        )}
        <div ref={postsAnnounceList.sentinelRef} />
      </div>
      <ScrollToTopButton />
    </>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default Topic
